import compact from "lodash/compact";
import { OpenGraphMedia } from "next-seo/lib/types";
import {
  AdvertiserContentArticle,
  BlogPosting,
  BreadcrumbList,
  Comment,
  DiscussionForumPosting,
  ImageObject,
  ListItem,
  LiveBlogPosting,
  NewsArticle,
  Organization,
  Person,
  VideoGame,
  VideoObject,
  WithContext,
  Product,
  ItemList,
  Offer,
  CreativeWork,
  Restaurant,
  SportsEvent,
} from "schema-dts";

import {
  GQLAuthorProfileResourceType,
  GQLVenueResourceType,
  GQLVideoGameResourceType,
  GQLMapResourceType,
  Maybe,
} from "@framework/__generated__/graphql/schema";
import { ThumbnailMimeTypeEnum } from "@framework/__generated__/relay/ArticleSEOContainer_feedContentResource.graphql";
import { SEOSchemasContainer_feedContentResource$data } from "@framework/__generated__/relay/SEOSchemasContainer_feedContentResource.graphql";
import { AttachmentType, FanPost, QuickPost } from "@framework/cyclone/types";
import {
  SOCIAL_IMAGE_MAX_WIDTH,
  getThumbnailDimensions,
} from "@voxmedia/duet-utils/images";

type ArticleBase = Pick<
  SEOSchemasContainer_feedContentResource$data,
  | "title"
  | "seo"
  | "dek"
  | "originalPublishedAt"
  | "publishedAt"
  | "streamPublishedAt"
  | "permalink"
  | "authors"
  | "categories"
  | "ledeMedia"
  | "isPaywallEligible"
> & { primaryCategory: { title: string } };

type Article = ArticleBase &
  Pick<SEOSchemasContainer_feedContentResource$data, "seoArticleBody">;

type LiveBlogUpdate = ArticleBase & {
  excerpt?: readonly {
    readonly paragraphContents: readonly {
      readonly plaintext: string;
    }[];
  }[];
};

type LiveBlog = Pick<
  SEOSchemasContainer_feedContentResource$data,
  "liveStatus" | "streamPosts"
> &
  Article;

export type PersonType = Pick<
  GQLAuthorProfileResourceType,
  "name" | "title" | "permalink"
> & {
  readonly profileImage: {
    readonly thumbnails: {
      readonly square: {
        readonly url: string;
      };
    };
  } | null;
};

interface Logo {
  url?: string;
  width?: number;
  height?: number;
}

interface BreadcrumbCategory {
  category: string;
  url: string;
}

export const publisherSchema = (
  name: string,
  logo: Logo | null,
): Organization => ({
  "@type": "Organization",
  name: name,
  // TODO (dcorrigan): implement logo when its available from community config
  logo: {
    "@type": "ImageObject",
    url: logo?.url || "",
    width: logo?.width?.toString() || "250",
    height: logo?.height?.toString() || "250",
  },
});

const imageSchema = (
  image: { originalHeight: number; originalWidth: number },
  thumbnail: {
    url: string;
    cropCoordinates: { height: number; width: number };
  },
): ImageObject => {
  const { height, width } = getThumbnailDimensions(image, thumbnail);
  return {
    "@type": "ImageObject",
    url: thumbnail.url,
    height: height.toString(),
    width: width.toString(),
  };
};

type ArticleSchemaBase = Omit<BlogPosting, "@type">;

const articleBaseProperties = (
  article: ArticleBase,
  publisher: Organization,
): ArticleSchemaBase => {
  const schema: ArticleSchemaBase = {
    headline: article.title,
    description: article.seo.description,
    datePublished: article.originalPublishedAt || "",
    dateModified: article.streamPublishedAt || article.publishedAt || "",
    url: article.permalink,
    mainEntityOfPage: article.permalink,
    publisher,
    articleSection: article.primaryCategory.title,
    keywords: article.categories.map((c) => c.title),
    author: article.authors.map((aut) => ({
      "@type": "Person",
      name: aut.name || "",
      url: aut.permalink,
    })),
  };

  if (article.isPaywallEligible) {
    schema.isAccessibleForFree = false;
  }
  if (article.ledeMedia?.__typename === "LedeMediaImageType") {
    schema.image = [
      imageSchema(
        article.ledeMedia.image,
        article.ledeMedia.image.thumbnails.social,
      ),
      imageSchema(
        article.ledeMedia.image,
        article.ledeMedia.image.thumbnails.horizontal,
      ),
      imageSchema(
        article.ledeMedia.image,
        article.ledeMedia.image.thumbnails.square,
      ),
    ];
  }

  return schema;
};

const articleProperties = (
  article: Article,
  publisher: Organization,
  breadcrumbCategories: BreadcrumbCategory[],
): Omit<BlogPosting, "@type"> => {
  return {
    ...articleBaseProperties(article, publisher),
    speakable: {
      "@type": "SpeakableSpecification",
      xpath: [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content",
      ],
    },
    articleBody: article.seoArticleBody,
    about: breadcrumbCategories.map((c) => ({
      "@type": "Thing",
      name: c.category,
      url: c.url || "",
    })),
  };
};

export const blogPostingSchema = (
  article: LiveBlogUpdate,
  publisher: Organization,
): BlogPosting => {
  const schema: BlogPosting = {
    "@type": "BlogPosting",
    ...articleBaseProperties(article, publisher),
  };

  if (article.excerpt) {
    schema.articleBody = article.excerpt
      .flatMap((e) => e.paragraphContents.map((c) => c.plaintext))
      .join("\n");
  }

  return schema;
};

export const liveBlogPostingSchema = (
  article: LiveBlog,
  publisher: Organization,
  breadcrumbCategories: BreadcrumbCategory[],
): WithContext<LiveBlogPosting> => {
  const schema: WithContext<LiveBlogPosting> = {
    "@context": "https://schema.org",
    "@type": "LiveBlogPosting",
    ...articleProperties(article, publisher, breadcrumbCategories),
    coverageStartTime: article.liveStatus.startAt || "",
  };

  if (article.liveStatus.endAt) {
    schema.coverageEndTime = article.liveStatus.endAt;
  }

  if (article.streamPosts) {
    const streamPosts = article.streamPosts.nodes;

    schema.liveBlogUpdate = compact(streamPosts).map((post) =>
      blogPostingSchema(post, publisher),
    );
  }

  return schema;
};

export const newsArticleSchema = (
  article: Article,
  publisher: Organization,
  breadcrumbCategories: BreadcrumbCategory[],
): WithContext<NewsArticle> => {
  const schema: WithContext<NewsArticle> = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    ...articleProperties(article, publisher, breadcrumbCategories),
  };

  return schema;
};

export const advertiserContentArticleSchema = (
  article: Article,
  publisher: Organization,
  breadcrumbCategories: BreadcrumbCategory[],
): WithContext<AdvertiserContentArticle> => {
  const schema: WithContext<AdvertiserContentArticle> = {
    "@context": "https://schema.org",
    "@type": "AdvertiserContentArticle",
    ...articleProperties(article, publisher, breadcrumbCategories),
  };

  return schema;
};

export const breadcrumbListSchema = (
  categories: BreadcrumbCategory[],
): WithContext<BreadcrumbList> => {
  const schema: WithContext<BreadcrumbList> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: categories.map(
      (c, idx): ListItem => ({
        "@type": "ListItem",
        position: idx + 1,
        name: c.category,
        item: c.url,
      }),
    ),
  };

  return schema;
};

export const personSchema = (profile: PersonType): WithContext<Person> => {
  const schema: WithContext<Person> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.name,
    ...(profile.title.length > 0 ? { jobTitle: profile.title } : {}),
    // TODO (dcorrigan): validate this with SEO team; could also be longBio or
    // shortBio
    // FIXME (tkstang): SEO was removed from author profile so we either need to add back seo fields
    // or just an SEO description field, or we need to figure out an alternate source of description
    // description: profile.seo?.description,
    image: profile.profileImage?.thumbnails.square.url,
    url: profile.permalink,
  };

  return schema;
};

interface ThumbnailType {
  readonly url: string;
}

type VideoBlock = {
  caption: { plaintext: string };
  video?: Maybe<{
    title: string;
    publishedAt: string | null;
    poster?: Maybe<{
      readonly thumbnails: {
        horizontal: ThumbnailType;
      };
    }>;
  }>;
};

export const videoObjectSchema = (
  videoBlock: VideoBlock,
  contentUrl: string,
): WithContext<VideoObject> => {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: videoBlock.video?.title,
    description: videoBlock.caption.plaintext,
    thumbnailUrl: videoBlock.video?.poster?.thumbnails.horizontal.url,
    uploadDate: videoBlock.video?.publishedAt || "",
    contentUrl,
  };
};

type VideoGameResource = Pick<
  GQLVideoGameResourceType,
  "title" | "releaseDate" | "permalink" | "genre" | "publisher"
> & {
  readonly seo: {
    readonly description: string;
  };
  readonly dek: {
    readonly plaintext: string;
  };
  readonly ledeMedia: {
    readonly image?: {
      readonly thumbnails: {
        readonly social: {
          readonly url: string;
        };
      };
    };
  } | null;
  readonly featuredVideo: {
    readonly videoDescription: {
      readonly plaintext: string;
    };
    readonly videoTitle: string;
    readonly videoUrl: string;
  } | null;
  readonly platforms: ReadonlyArray<{
    readonly title: string;
  }>;
  readonly gameGuides: {
    readonly posts: {
      readonly nodes: ReadonlyArray<{
        readonly permalink?: string;
        readonly title?: string;
      }>;
    } | null;
  } | null;
};

export const videoGameSchema = (
  videoGameResource: VideoGameResource,
): WithContext<VideoGame> => {
  const schema: WithContext<VideoGame> = {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    name: videoGameResource.title,
    url: videoGameResource.permalink,
    gamePlatform: videoGameResource.platforms.map((p) => p.title),
    publisher: videoGameResource.publisher,
  };

  if (videoGameResource.featuredVideo) {
    schema.trailer = {
      "@type": "VideoObject",
      contentUrl: videoGameResource.featuredVideo.videoUrl,
    };

    const videoDescription =
      videoGameResource.featuredVideo.videoDescription.plaintext;
    if (videoDescription.length > 0) {
      schema.trailer.description = videoDescription;
    }

    const videoName = videoGameResource.featuredVideo.videoTitle;
    if (videoName.length > 0) {
      schema.trailer.name = videoName;
    }
  }

  if (videoGameResource.releaseDate) {
    schema.dateCreated = videoGameResource.releaseDate;
    schema.datePublished = videoGameResource.releaseDate;
  }

  const seoDescription = videoGameResource.seo.description;
  const description =
    seoDescription.length > 0
      ? seoDescription
      : videoGameResource.dek.plaintext;
  if (description.length > 0) {
    schema.description = description;
  }

  const image = videoGameResource.ledeMedia?.image?.thumbnails.social.url;
  if (image) {
    schema.image = image;
  }

  if (videoGameResource.genre.length > 0) {
    schema.genre = videoGameResource.genre.join(", ");
  }

  const gameGuides = videoGameResource.gameGuides?.posts?.nodes;
  if (gameGuides && gameGuides.length > 0) {
    schema.gameTip = gameGuides.reduce((tips: CreativeWork[], gg) => {
      if (gg.title && gg.permalink) {
        tips.push({
          "@type": "CreativeWork",
          headline: gg.title,
          url: gg.permalink,
        });
      }
      return tips;
    }, []);
  }

  return schema;
};

type VenueResource = Pick<
  GQLVenueResourceType,
  "title" | "permalink" | "url" | "phone" | "location"
> & {
  readonly seo: {
    readonly description: string;
  };
  readonly dek: {
    readonly plaintext: string;
  };
  readonly location: {
    readonly latitude: number;
    readonly longitude: number;
  } | null;
  readonly safegraph: {
    readonly website: string | null;
    readonly streetAddress: string | null;
    readonly city: string | null;
    readonly region: string | null;
    readonly postalCode: string | null;
    readonly isoCountryCode: string | null;
  } | null;
};

export const venueSchema = (
  venueResource: VenueResource,
): WithContext<Restaurant> => {
  const schema: WithContext<Restaurant> = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: venueResource.title,
    description:
      venueResource.seo.description.length > 0
        ? venueResource.seo.description
        : venueResource.dek.plaintext,
    url: venueResource.permalink,
    telephone: venueResource.phone || undefined,
    sameAs: venueResource.safegraph?.website || undefined,
  };

  if (venueResource.location) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: `${venueResource.location.latitude}`,
      longitude: `${venueResource.location.longitude}`,
    };
  }

  if (venueResource.safegraph) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: venueResource.safegraph.streetAddress || undefined,
      addressLocality: venueResource.safegraph.city || undefined,
      addressRegion: venueResource.safegraph.region || undefined,
      postalCode: venueResource.safegraph.postalCode || undefined,
      addressCountry: venueResource.safegraph.isoCountryCode || undefined,
    };
  }

  return schema;
};

type MapPoint = {
  name: string;
  url: string;
  image?: string;
};

type Map = Pick<
  GQLMapResourceType,
  "title" | "dek" | "seo" | "permalink" | "originalPublishedAt" | "publishedAt"
>;

export const itemListMapSchema = (
  map: Map,
  items: MapPoint[],
): WithContext<ItemList> => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    mainEntityOfPage: map.permalink,
    name: map.seo.headline || map.title,
    description: map.seo.description || map.dek.plaintext,
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Restaurant",
        name: item.name,
        url: item.url,
        image: item.image,
      },
    })),
  };
};

type ProductBlock = {
  title: string;
  score: number;
  showScores: boolean;
  brand: {
    title: string;
  } | null;
  retailLinks?: readonly {
    readonly price: number;
    readonly retailer: string;
    readonly salePrice: number;
    readonly url: string;
  }[];
  image: {
    readonly thumbnails: {
      readonly horizontal: {
        readonly url: string;
      };
    };
  } | null;
};

export const productObjectSchema = (
  product: ProductBlock,
  authors: readonly PersonType[],
): WithContext<Product> => {
  const schema: WithContext<Product> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
  };

  if (product.image) {
    schema.image = product.image.thumbnails.horizontal.url;
  }

  if (product.retailLinks && product.retailLinks.length > 0) {
    schema.offers = product.retailLinks.map(
      (retailer): Offer => ({
        "@type": "Offer",
        priceCurrency: "USD",
        price: retailer.salePrice || retailer.price,
        url: retailer.url,
        seller: {
          "@type": "Organization",
          name: retailer.retailer,
        },
      }),
    );
  }

  if (product.brand) {
    schema.brand = {
      "@type": "Brand",
      name: product.brand.title,
    };
  }

  if (product.showScores) {
    schema.review = {
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: product.score,
        bestRating: 10,
        worstRating: 1,
      },
      author: authors.map((a) => personSchema(a)),
    };
  }

  return schema;
};

export const itemListProductSchema = (
  items: readonly ProductBlock[],
  authors: readonly PersonType[],
): WithContext<ItemList> => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: productObjectSchema(item, authors),
    })),
  };
};

const getThumbnailMimetype = (type: ThumbnailMimeTypeEnum) => {
  switch (type) {
    case "imageGif":
      return "image/gif";
    case "imageJpg":
      return "image/jpeg";
    case "imagePng":
      return "image/png";
    case "imageSvgXml":
      return "image/svg+xml";
    case "imageWebp":
      return "image/webp";
  }
};

export const buildOgImageData = ({
  image,
  imageThumbnail,
  placeholderImageUrl,
}: {
  image?: {
    title: string;
    alt: string;
    originalWidth: number;
    originalHeight: number;
  } | null;
  imageThumbnail?: {
    url: string;
    mimeType: ThumbnailMimeTypeEnum;
    normalizedRatio: number;
    cropCoordinates: { height: number; width: number };
  } | null;
  placeholderImageUrl: string;
}): OpenGraphMedia => {
  if (image && imageThumbnail) {
    const { width, height } = getThumbnailDimensions(image, imageThumbnail);
    const { normalizedRatio } = imageThumbnail;

    let socialWidth = width;
    let socialHeight = height;
    let socialUrl = imageThumbnail.url;

    if (width > SOCIAL_IMAGE_MAX_WIDTH) {
      socialWidth = SOCIAL_IMAGE_MAX_WIDTH;
      socialHeight = Math.floor(SOCIAL_IMAGE_MAX_WIDTH / normalizedRatio);

      const url = new URL(socialUrl);
      const params = new URLSearchParams(url.search);

      if (!params.has("w")) {
        params.set("w", `${SOCIAL_IMAGE_MAX_WIDTH}`);
        url.search = params.toString();
        socialUrl = url.toString();
      }
    }

    const ogAlt = image.alt.length > 0 ? image.alt : image.title;

    // TODO: [WPSCHEMA] verify this data is all parity with Tower
    return {
      url: socialUrl,
      height: socialHeight,
      width: socialWidth,
      alt: ogAlt,
      type: getThumbnailMimetype(imageThumbnail.mimeType),
    };
  } else {
    return {
      url: placeholderImageUrl,
    };
  }
};

export type MMAFighterResource = {
  title: string;
  permalink: string;
  birthDate?: string;
  birthPlace?: string;
};

export const mmaFighterSchema = (
  mmaFighterResource: MMAFighterResource,
): WithContext<Person> => {
  const schema: WithContext<Person> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: mmaFighterResource.title,
    url: mmaFighterResource.permalink,
  };

  if (mmaFighterResource.birthDate) {
    schema.birthDate = mmaFighterResource.birthDate;
  }
  if (mmaFighterResource.birthPlace) {
    schema.birthPlace = mmaFighterResource.birthPlace;
  }

  return schema;
};

export type MMAFightResource = {
  title: string;
  permalink: string;
  fighterA?: MMAFighterResource;
  fighterB?: MMAFighterResource;
  event?: MMAEventResource;
};

export const mmaFightSubSchema = (
  mmaFightResource: MMAFightResource,
): WithContext<SportsEvent> => {
  const schema: WithContext<SportsEvent> = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: mmaFightResource.title,
    url: mmaFightResource.permalink,
  };
  if (mmaFightResource.fighterA && mmaFightResource.fighterB) {
    schema.competitor = [
      mmaFighterSchema(mmaFightResource.fighterA),
      mmaFighterSchema(mmaFightResource.fighterB),
    ];
  }
  return schema;
};

export const mmaFightCompleteSchema = (
  mmaFightResource: MMAFightResource,
): WithContext<SportsEvent> => {
  const schema = mmaFightSubSchema(mmaFightResource);
  if (mmaFightResource.event) {
    schema.superEvent = mmaEventSchema(mmaFightResource.event);
  }
  return schema;
};

export type MMAEventResource = {
  title: string;
  permalink: string;
  location?: string;
  eventStartDate?: string;
  fights?: MMAFightResource[];
  promotion?: {
    name: string;
    slug: string;
  };
};

export const mmaEventSchema = (
  mmaEventResource: MMAEventResource,
): WithContext<SportsEvent> => {
  const schema: WithContext<SportsEvent> = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: mmaEventResource.title,
    url: mmaEventResource.permalink,
    startDate: mmaEventResource.eventStartDate || "",
    location: mmaEventResource.location || "",
  };
  if (mmaEventResource.fights && mmaEventResource.fights.length > 0) {
    schema.subEvent = mmaEventResource.fights.map((fight) =>
      mmaFightSubSchema(fight),
    );
  }
  if (mmaEventResource.promotion) {
    schema.organizer = {
      "@type": "Organization",
      legalName: mmaEventResource.promotion.name,
      alternateName: mmaEventResource.promotion.slug,
    };
  }
  return schema;
};

const getImageFromFeedContent = (
  post: FanPost | QuickPost,
): string | undefined => {
  if (post.attachments?.[0]?.type === AttachmentType.Upload) {
    return post.attachments[0].url;
  }
  return undefined;
};

type SchemaComment = QuickPost & { fullBody: string } & {
  comment: SchemaComment[];
};

const commentSchema = (reply: SchemaComment): Comment => {
  return {
    "@type": "Comment",
    author: {
      "@type": "Person",
      name: reply.author?.username || "",
      url: reply.author?.profileURL || "",
    },
    text: reply.fullBody,
    datePublished: reply.createdAt || "",
    commentCount: reply.replyCount,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: { "@type": "LikeAction" },
      name: "Likes",
      userInteractionCount: reply.recs,
    },
    image: getImageFromFeedContent(reply),
    comment: reply.comment.map((reply: SchemaComment) => commentSchema(reply)),
  };
};

export const discussionForumSchema = (
  fanPost: FanPost,
  canonicalPostPath: string,
  nestedSEOReplies?: Array<SchemaComment>,
  fanPostFullBody?: string | null,
): WithContext<DiscussionForumPosting> => {
  const schema: WithContext<DiscussionForumPosting> = {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    mainEntityOfPage: canonicalPostPath,
    url: canonicalPostPath,
    headline: fanPost.title,
    author: {
      "@type": "Person",
      name: fanPost.author?.username || "",
      url: fanPost.author?.profileURL || "",
    },
    datePublished: fanPost.createdAt,
    text: fanPostFullBody || fanPost.body,
    commentCount: fanPost.replyCount,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: { "@type": "LikeAction" },
      name: "Likes",
      userInteractionCount: fanPost.recs,
    },
    image: getImageFromFeedContent(fanPost),
    comment: nestedSEOReplies?.[0]?.comment.map((reply: SchemaComment) => {
      return commentSchema(reply);
    }),
  };
  return schema;
};
